/**
 * usePostMetadata.tsx - Post Metadata GraphQL Hook
 *
 * Provides GraphQL operations for fetching and saving post metadata.
 */

import { API, isLocalhost, swrLoadOnce, useApi } from "core"
import { TypedDocumentNode as DocumentNode } from '@graphql-typed-document-node/core';
import { GraphQLClient } from 'graphql-request'
import gql from "graphql-tag"
import { useCallback } from "react"
import useSWR, { SWRConfiguration } from "swr"

export type Exact<T extends { [key: string]: unknown }> = { [K in keyof T]: T[K] };

export interface Scalars {
    ID: { input: string; output: string; }
    String: { input: string; output: string; }
    Boolean: { input: boolean; output: boolean; }
    Int: { input: number; output: number; }
    Float: { input: number; output: number; }
    DateTime: { input: string; output: string; }
    JSON: { input: Record<string, any>; output: Record<string, any>; }
  }

export const createGraphQLClient = async (api: API): Promise<GraphQLClient> => {
    const jwt = await api.loadJwt()

    return new GraphQLClient(isLocalhost() ? "https://" + "staging.newssocial.uk" + '/main/graphql/' : "https://" + window.location.hostname + '/main/graphql/', {
        headers: {
            authorization: jwt && `Bearer ${jwt}`,
        },
    })
}

export const authorizationHeader = async (api: API) => {
    const jwt = await api.loadJwt()
    return {
        authorization: jwt && `Bearer ${jwt}`,
    }
}

export const useGraphQLClientFn = () => {
    const api = useApi()
    return useCallback(() => {
        return createGraphQLClient(api)
    }, [api])

}

import { useState } from "react"

export const useGraphQLErrorHandling = () => {
    const [errorMessages, setErrorMessages] = useState<string[]>([])

    const handleGraphQLErrors = (error: any): string[] => {
        let messages
        if (error && error.response && error.response.errors) {
            messages = error.response.errors.map((err: any) => err.message)
            setErrorMessages(messages)
        } else {
            messages = ["An unexpected error occurred. Please try again later."]
            setErrorMessages(messages)
        }
        return messages
    }

    const clearErrors = () => {
        setErrorMessages([])
    }

    return {
        errorMessages,
        handleGraphQLErrors,
        clearErrors,
    }
}

export type GetPostMetadataQueryVariables = Exact<{
    postId: Scalars['ID']['input'];
  }>;

export type GetPostMetadataQuery = { __typename?: 'Query', post?: { __typename?: 'PostType', _id: string, title: string, content?: Record<string, any> | null | undefined, metadata?: Record<string, any> | null | undefined, created: string, tags: Array<string>, author: { __typename?: 'UserType', lastname?: string | null | undefined, firstname?: string | null | undefined, username?: string | null | undefined }, group: { __typename?: 'PostGroupType', group: { __typename?: 'GroupType', _id: string } } } | null | undefined };

export const GetPostMetadataDocument = {"kind":"Document","definitions":[{"kind":"OperationDefinition","operation":"query","name":{"kind":"Name","value":"GetPostMetadata"},"variableDefinitions":[{"kind":"VariableDefinition","variable":{"kind":"Variable","name":{"kind":"Name","value":"postId"}},"type":{"kind":"NonNullType","type":{"kind":"NamedType","name":{"kind":"Name","value":"ID"}}}}],"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"post"},"arguments":[{"kind":"Argument","name":{"kind":"Name","value":"postId"},"value":{"kind":"Variable","name":{"kind":"Name","value":"postId"}}}],"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"_id"}},{"kind":"Field","name":{"kind":"Name","value":"title"}},{"kind":"Field","name":{"kind":"Name","value":"author"},"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"lastname"}},{"kind":"Field","name":{"kind":"Name","value":"firstname"}},{"kind":"Field","name":{"kind":"Name","value":"username"}}]}},{"kind":"Field","name":{"kind":"Name","value":"content"}},{"kind":"Field","name":{"kind":"Name","value":"group"},"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"group"},"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"_id"}}]}}]}},{"kind":"Field","name":{"kind":"Name","value":"metadata"}},{"kind":"Field","name":{"kind":"Name","value":"created"}},{"kind":"Field","name":{"kind":"Name","value":"tags"}}]}}]}}]} as unknown as DocumentNode<GetPostMetadataQuery, GetPostMetadataQueryVariables>;

export const GET_POST_METADATA = gql`
  query GetPostMetadata($postId: ID!) {
    post(postId: $postId) {
        _id
        title
        author {
            lastname
            firstname
            username
        }
        content
        group{
            group {
                _id
            }
        }
        metadata
        created
        tags
    }
  }
`

export const SAVE_POST_METADATA = gql`
  mutation savePostMetadata($postId: ID!, $metadata: JSON!) {
    savePostMetadata(postId: $postId, metadata: $metadata ) {
      _id
    }
  }
`

export const usePostMetadata = (postId: string, op?: SWRConfiguration) => {
    const graphQLClient = useGraphQLClientFn()

    const result = useSWR<GetPostMetadataQuery>(postId ? [postId, GetPostMetadataDocument] : null, async () => {
        const graphqlClient = await graphQLClient()
        const result = await graphqlClient.request<GetPostMetadataQuery>(GetPostMetadataDocument, {
            postId,
        })
        return result
    }, {
        ...swrLoadOnce,
        ...op,
    })

    return result
}
